import { useEffect, useState } from "react"
import { Ban, Palette } from "lucide-react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useTranslation } from "react-i18next"

import {
  Input,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@f2e/ui/components/base"
import { FormErrorMessage } from "@f2e/ui/components/base-plus"

import { useToast, useSysConfig } from "@/hooks"
import {
  WorkspaceResponse,
  useCreateWorkspacesApi,
  useUpdateWorkspacesApi,
  CreateWorkspaceRequest,
  UpdateWorkspaceRequest,
  useGetBrandsApi,
  brandsResponseToBrands,
} from "@/hooks/api"
import { Response, Workspace, Brand } from "@/types"
import { useStore } from "@/store"
import { getLimitErrorTranslationKey } from "@/constants/errors"
import { getApiErrorData, getUserFacingApiErrorMessage } from "@/lib/apiErrors"
import { getResourceUrl } from "@/lib/utils"

interface WorkspaceDialogProps {
  open?: boolean
  editWorkspace?: Workspace
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
}

const formSchema = z.object({
  name: z.string().min(1).max(255),
  brandId: z.string().optional(),
})

type FormInput = z.infer<typeof formSchema>

const WorkspaceDialog = ({
  open = false,
  editWorkspace = undefined,
  onOpenChange,
  onConfirm,
}: WorkspaceDialogProps) => {
  const { t } = useTranslation()
  const config = useSysConfig()

  const { alertToast } = useToast()
  const currentOrganization = useStore((state) => state.currentOrganization)
  const [show, setShow] = useState(false)
  const [brands, setBrands] = useState<Brand[]>([])

  const form = useForm<FormInput>({
    resolver: zodResolver(formSchema),
    values: {
      name: editWorkspace?.name || "",
      brandId: editWorkspace?.brand?.id || "",
    },
  })

  const selectedBrandId = form.watch("brandId")
  const selectedBrand =
    selectedBrandId && selectedBrandId !== "__none__"
      ? brands.find((b) => b.id === selectedBrandId)
      : null

  // APIs
  const {
    error: createError,
    data: createRespData,
    fetchFn: createWorkspace,
  } = useCreateWorkspacesApi(currentOrganization!.id)
  const {
    error: updateError,
    data: updateRespData,
    fetchFn: updateWorkspace,
  } = useUpdateWorkspacesApi(
    currentOrganization!.id,
    editWorkspace?.id?.toString() || ""
  )
  const { data: brandsRespData, fetchFn: getBrands } = useGetBrandsApi(
    currentOrganization!.id
  )

  useEffect(() => {
    setShow(open || false)
  }, [open])

  // Fetch brands when opening in edit mode
  useEffect(() => {
    if (show && editWorkspace) {
      getBrands()
    }
  }, [show, editWorkspace, getBrands])

  useEffect(() => {
    if (!createError) return
    const errorData = getApiErrorData(createError) || createRespData
    const limitKey = getLimitErrorTranslationKey(
      (errorData as { error_code?: number })?.error_code
    )
    const description = limitKey
      ? t(limitKey)
      : getUserFacingApiErrorMessage(
          createError,
          t,
          `${
            (createRespData as { message?: string } | undefined)?.message ||
            t("workspace.message.createFailed")
          }`
        )
    alertToast({
      title: t("workspace.message.createFailed"),
      description,
    })
  }, [createError, createRespData, alertToast, t])

  useEffect(() => {
    if (!updateError) return
    alertToast({
      title: t("workspace.message.updateFailed"),
      description: getUserFacingApiErrorMessage(
        updateError,
        t,
        `${updateRespData?.message || t("workspace.message.updateFailed")}`
      ),
    })
  }, [updateError, updateRespData, alertToast, t])

  // Handle brands data
  useEffect(() => {
    if (!brandsRespData || !brandsRespData.data) return
    setBrands(brandsResponseToBrands(brandsRespData.data))
  }, [brandsRespData])

  const handleConfirmClick = (formData: FormInput) => {
    const isEditMode = editWorkspace !== undefined
    const callbackFn = (resp: Response<WorkspaceResponse> | undefined) => {
      if (resp) {
        onConfirm()
      }
    }

    if (isEditMode) {
      const reqData: UpdateWorkspaceRequest = {
        name: formData.name,
        brand_id:
          !formData.brandId ||
          formData.brandId === "__none__" ||
          formData.brandId === ""
            ? null
            : formData.brandId,
      }
      updateWorkspace({
        jsonBody: reqData,
        callbackFn: callbackFn,
      })
    } else {
      const reqData: CreateWorkspaceRequest = {
        name: formData.name,
        users: [],
      }
      createWorkspace({
        jsonBody: reqData,
        callbackFn: callbackFn,
      })
    }

    handleOpenChange(false)
  }

  const handleOpenChange = (open: boolean) => {
    onOpenChange?.(open)
    if (!open) {
      setShow(false)
      form.reset()
    }
  }

  return (
    <Dialog open={show} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        {editWorkspace ? (
          <DialogHeader>
            <DialogTitle>{t("workspace.workspace.edit.title")}</DialogTitle>
            <DialogDescription>
              {t("workspace.workspace.edit.description")}
            </DialogDescription>
          </DialogHeader>
        ) : (
          <DialogHeader>
            <DialogTitle>{t("workspace.workspace.new.title")}</DialogTitle>
            <DialogDescription>
              {t("workspace.workspace.new.description")}
            </DialogDescription>
          </DialogHeader>
        )}

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleConfirmClick)}
            className="space-y-8"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("workspace.name")}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormErrorMessage
                    message={t("workspace.message.nameRequired")}
                  />
                </FormItem>
              )}
            />
            {editWorkspace && (
              <FormField
                control={form.control}
                name="brandId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("workspace.brand", { defaultValue: "Brand" })}
                    </FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ?? ""}
                    >
                      <FormControl>
                        <SelectTrigger className="min-w-0 [&>span]:min-w-0 [&>span]:overflow-hidden">
                          <SelectValue
                            placeholder={t("workspace.brandPlaceholder", {
                              defaultValue: "Select a brand (optional)",
                            })}
                          >
                            {!selectedBrandId ||
                            selectedBrandId === "__none__" ? (
                              <div className="flex items-center gap-3 w-full min-w-0">
                                <Ban className="h-5 w-5 shrink-0 text-muted-foreground" />
                                <span className="text-sm font-medium truncate flex-1 min-w-0">
                                  {t("workspace.brandNone", {
                                    defaultValue: "No brand",
                                  })}
                                </span>
                              </div>
                            ) : selectedBrand ? (
                              <div className="flex items-center gap-3 w-full min-w-0">
                                {selectedBrand.logo ? (
                                  <img
                                    src={getResourceUrl(
                                      selectedBrand.logo,
                                      config.serverRoot
                                    )}
                                    alt={selectedBrand.name}
                                    className="h-5 w-5 shrink-0 rounded"
                                  />
                                ) : (
                                  <Palette className="h-5 w-5 shrink-0 text-muted-foreground" />
                                )}
                                <span className="text-sm font-medium truncate flex-1 min-w-0">
                                  {selectedBrand.name}
                                </span>
                              </div>
                            ) : null}
                          </SelectValue>
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="max-w-(--radix-select-trigger-width)">
                        <SelectItem
                          value="__none__"
                          className="py-2 min-w-0 [&>*:last-child]:min-w-0 [&>*:last-child]:w-full [&>*:last-child]:overflow-hidden"
                        >
                          <div className="flex items-center gap-3 w-full min-w-0">
                            <Ban className="h-8 w-8 shrink-0 text-muted-foreground" />
                            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                              <span className="text-sm font-medium truncate">
                                {t("workspace.brandNone", {
                                  defaultValue: "No brand",
                                })}
                              </span>
                            </div>
                          </div>
                        </SelectItem>
                        {brands.length > 0 &&
                          brands.map((b) => (
                            <SelectItem
                              key={b.id}
                              value={b.id}
                              className="py-2 min-w-0 [&>*:last-child]:min-w-0 [&>*:last-child]:w-full [&>*:last-child]:overflow-hidden"
                            >
                              <div className="flex items-center gap-3 w-full min-w-0">
                                {b.logo ? (
                                  <img
                                    src={getResourceUrl(
                                      b.logo,
                                      config.serverRoot
                                    )}
                                    alt={b.name}
                                    className="h-8 w-8 shrink-0 rounded"
                                  />
                                ) : (
                                  <Palette className="h-8 w-8 shrink-0 text-muted-foreground" />
                                )}
                                <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                                  <span className="text-sm font-medium truncate">
                                    {b.name}
                                  </span>
                                  {b.description && (
                                    <span className="text-xs text-muted-foreground truncate">
                                      {b.description}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            )}
            <DialogFooter>
              <Button type="submit">
                {editWorkspace ? t("workspace.save") : t("workspace.create")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default WorkspaceDialog
